/* A small picture of a report page: every visual's box on the 24-column TV
 * grid, coloured by kind (numbers, charts, share charts, tables, filters,
 * text). Used for templates, suggestions and the New report dialog. */
import type { LayoutSummary } from "../api/v21";

export type VisualKind = "number" | "chart" | "share" | "table" | "filter" | "content";

const KIND: Record<string, VisualKind> = {
  kpi: "number", gauge: "number", gauge_chart: "number", progress: "number",
  bar_chart: "chart", line_chart: "chart", area_chart: "chart", combo_chart: "chart", waterfall_chart: "chart", scatter_chart: "chart", heatmap_chart: "chart",
  pie_chart: "share", donut_chart: "share", treemap_chart: "share", funnel_chart: "share", map: "share",
  table: "table", matrix: "table", grouped_table: "table", status_list: "table", alert_list: "table", news_feed: "table",
  filter: "filter", date_filter: "filter", text_filter: "filter",
};

export const KIND_STYLE: Record<VisualKind, { fill: string; label: string; one: string; many: string }> = {
  number: { fill: "#3b9bff", label: "Numbers", one: "KPI", many: "KPIs" },
  chart: { fill: "#2dd4bf", label: "Charts", one: "chart", many: "charts" },
  share: { fill: "#a78bfa", label: "Share charts", one: "share chart", many: "share charts" },
  table: { fill: "#94a3b8", label: "Tables", one: "table", many: "tables" },
  filter: { fill: "#fbbf24", label: "Filters", one: "filter", many: "filters" },
  content: { fill: "#e2e8f0", label: "Text & panels", one: "text block", many: "text blocks" },
};

export function kindOf(type: string): VisualKind {
  return KIND[type] ?? "content";
}

/** "4 KPIs · 2 charts · 1 table" */
export function describeVisuals(types: Record<string, number>): string {
  const counts: Partial<Record<VisualKind, number>> = {};
  for (const [t, n] of Object.entries(types)) counts[kindOf(t)] = (counts[kindOf(t)] ?? 0) + n;
  const order: VisualKind[] = ["number", "chart", "share", "table", "filter", "content"];
  const parts = order.filter((k) => counts[k]).map((k) => `${counts[k]} ${counts[k] === 1 ? KIND_STYLE[k].one : KIND_STYLE[k].many}`);
  return parts.length ? parts.join(" · ") : "Empty page";
}

export function LayoutThumb({ summary, page = 0, height = 96, empty = "Empty page", title }: { summary: LayoutSummary | null | undefined; page?: number; height?: number; empty?: string; title?: string }) {
  const p = summary?.pages?.[page];
  const cols = p?.columns ?? 24;
  const rows = Math.max(p?.rows ?? 34, ...(p?.widgets ?? []).map((w) => w.y + w.h));
  const W = 240;
  const H = Math.round((W * 944) / 1848);
  const filterStrip = p?.filters ? 6 : 0;
  const cw = W / cols;
  const rh = (H - filterStrip) / rows;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title ?? "Layout preview"} style={{ display: "block", width: "100%", height: "auto", maxHeight: height * 2, aspectRatio: `${W} / ${H}`, borderRadius: 8, background: "#0a1628" }}>
      {title ? <title>{title}</title> : null}
      {p?.filters ? Array.from({ length: Math.min(p.filters, 5) }).map((_, i) => <rect key={`f${i}`} x={4 + i * 26} y={1.5} width={22} height={3.5} rx={1.5} fill={KIND_STYLE.filter.fill} opacity={0.85} />) : null}
      {!p || p.widgets.length === 0 ? (
        <>
          <rect x={8} y={8} width={W - 16} height={H - 16} rx={6} fill="none" stroke="rgba(255,255,255,0.28)" strokeDasharray="5 4" />
          <text x={W / 2} y={H / 2 + 4} textAnchor="middle" fontSize={11} fill="rgba(255,255,255,0.55)" fontFamily="system-ui, sans-serif">{empty}</text>
        </>
      ) : (
        p.widgets.map((w, i) => {
          const k = kindOf(w.type);
          return (
            <rect
              key={i}
              x={w.x * cw + 1.2}
              y={filterStrip + w.y * rh + 1.2}
              width={Math.max(1, w.w * cw - 2.4)}
              height={Math.max(1, w.h * rh - 2.4)}
              rx={2.5}
              fill={KIND_STYLE[k].fill}
              opacity={k === "content" ? 0.35 : k === "table" ? 0.55 : 0.8}
            />
          );
        })
      )}
    </svg>
  );
}

export function KindLegend() {
  return (
    <div className="ds-row" style={{ gap: 12, flexWrap: "wrap" }}>
      {(Object.keys(KIND_STYLE) as VisualKind[]).map((k) => (
        <span key={k} className="ds-small ds-muted" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: KIND_STYLE[k].fill, display: "inline-block" }} />
          {KIND_STYLE[k].label}
        </span>
      ))}
    </div>
  );
}
